'use client';

import { motion, AnimatePresence } from 'framer-motion';

interface IPRightsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function IPRightsModal({ isOpen, onClose }: IPRightsModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl border-4 border-black overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-10 w-12 h-12 bg-oxford-blue text-white rounded-full flex items-center justify-center hover:bg-blue-crayola transition-colors duration-200 border-2 border-black"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Content - Scrollable */}
            <div className="overflow-y-auto max-h-[90vh] styled-scrollbar">
              <div className="px-6 py-8 md:px-12 md:py-12">
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-2xl md:text-3xl lg:text-4xl text-center mb-8 md:mb-12 font-display font-bold text-oxford-blue uppercase"
                >
                  IP RIGHTS OUR NOT OURS
                </motion.h1>
                
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="space-y-8"
                >
                  <div className="content-card">
                    <h2 className="button-text mb-4 text-oxford-blue">
                      ADDENDUM: COMMUNITY TOKEN INITIATIVE
                    </h2>
                    <p className="body-text text-text leading-relaxed">
                      This Addendum supplements and affirms the existing agreement set forth above. It is incorporated 
                      herein by reference and applies to any use or association of a Pudgy Penguins NFT with the Eureka 
                      CTO, a community-led project utilizing Pudgy Penguins-related art and branding elements 
                      exclusively in accordance with the guidelines provided by LSLTTT Holdings Inc., including but not 
                      limited to the Pudgy Penguins Brand Bible and Universe guidelines.
                    </p>
                  </div>

                  <div className="content-card">
                    <h2 className="button-text mb-4 text-oxford-blue">
                      A. OWNERSHIP AND RECOGNITION
                    </h2>
                    <div className="space-y-4 body-text text-text">
                      <p>
                        All intellectual property rights, trademarks, copyrights, and associated branding for Pudgy Penguins 
                        NFTs and related artwork are and shall remain entirely and exclusively the property of the 
                        Creator, LSLTTT Holdings Inc. ("Creator").
                      </p>
                      <p>
                        Neither this community-driven initiative, nor any of its organizers, members, or participants, claim 
                        or assert ownership, partnership, or affiliation with the Pudgy Penguins IP. This project is not 
                        endorsed, sponsored, or formally affiliated with the Creator, unless and until such endorsement is 
                        explicitly granted in writing by the Creator.
                      </p>
                      <p>
                        The purpose of the initiative is to support the growth, awareness, and goodwill of the Pudgy 
                        Penguins universe through aligned, compliant, and respectful use of the NFT artwork owned by 
                        individual community members.
                      </p>
                    </div>
                  </div>

                  <div className="content-card">
                    <h2 className="button-text mb-4 text-oxford-blue">
                      B. LICENSED USAGE UNDER STRICT COMPLIANCE
                    </h2>
                    <div className="space-y-4 body-text text-text">
                      <p>
                        The community token project utilizes one or more Pudgy Penguins NFTs under the limited, non
                        exclusive licenses granted to the individual Primary Owner(s) under Sections 3 and 4 of the original 
                        agreement above. No broader rights are claimed beyond those explicitly granted by the Creator.
                      </p>
                      <p>
                        All branding, promotional content, and visuals deployed by this community initiative are created in 
                        strict adherence to the branding guidelines and the Pudgy Penguins Brand Bible. No 
                        deviations, alterations, or unauthorized interpretations of the Pudgy Penguins characters, themes, or 
                        style guides will be made.
                      </p>
                      <p>
                        This initiative respects and follows the annual revenue threshold limitations (currently $500,000), 
                        and commits to notifying the Creator and seeking additional licenses or exemptions in accordance 
                        with the process laid out in the original agreement if applicable.
                      </p>
                    </div>
                  </div>

                  <div className="content-card">
                    <h2 className="button-text mb-4 text-oxford-blue">
                      C. NON-COMMERCIAL INTENT & IP RESPECT
                    </h2>
                    <div className="space-y-4 body-text text-text">
                      <p>
                        The sole intent of this initiative is to foster community engagement, increase visibility for Pudgy 
                        Penguins, and celebrate the Creator's IP through respectful and aligned efforts. The project does not 
                        seek to commercialize the Pudgy Penguins brand or compete with the Creator's own initiatives.
                      </p>
                      <h3 className="text-lg font-bold text-oxford-blue mb-3">To that end:</h3>
                      <ul className="list-disc list-inside space-y-2 pl-4">
                        <li>We are committed to not registering trademarks, filing for IP protection, or implying any brand ownership.</li>
                        <li>We will not use the name "Pudgy Penguins," its logos, or proprietary marks beyond factual references, and only where permitted under the agreement.</li>
                        <li>We acknowledge and support the Creator's rights to audit, review, and direct any corrective actions necessary to ensure continued compliance with its licensing structure.</li>
                      </ul>
                    </div>
                  </div>

                  <div className="content-card">
                    <h2 className="button-text mb-4 text-oxford-blue">
                      D. INDEMNITY AND LEGAL COMPLIANCE
                    </h2>
                    <div className="space-y-4 body-text text-text">
                      <p>
                        All members and organizers of the community token project agree to fully indemnify and hold 
                        harmless the Creator from any legal or financial liability arising from any breach or unauthorized 
                        use that may occur within this initiative.
                      </p>
                      <p>
                        We further agree to promptly cease and desist from any activity if so requested by the Creator, and 
                        to work collaboratively with the Creator to resolve any concerns or violations in good faith.
                      </p>
                    </div>
                  </div>

                  <div className="content-card">
                    <h2 className="button-text mb-4 text-oxford-blue">
                      E. CONTINUING SUPPORT AND GOODWILL
                    </h2>
                    <div className="space-y-4 body-text text-text">
                      <p>
                        This initiative is born from admiration for the Pudgy Penguins universe. It exists to promote the 
                        broader IP's growth, create positive community interaction, and help expand the brand's cultural 
                        relevance under the direct vision and leadership of LSLTTT Holdings Inc.
                      </p>
                      <p>
                        We invite and welcome any feedback from the Creator and are open to adjusting, pausing, or 
                        ceasing activities based on such input. This project's foundation is mutual respect, support, and 
                        compliance.
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
} 